import React, { useContext, useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Radio,
    Button,
    Space,
    Spin,
    Typography,
} from 'antd';
import { debounce } from 'lodash';
import { ToursContext } from '../../context/tours.context';
import { ETourTargetType } from '../../../../enums/tours.enum';
import { getWorkspaces } from '../../../../api/workspaces';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const PAGE_SIZE = 20;

const TourInitialForm: React.FC = () => {
    const {
        createTour,
        countries,
        loadingCountries,
        completeTourConfiguration,
        fetchWorkspacesAsync,
        cancelAddingTour,
        currentTour,
        tourSelected,
        currentPath,
    } = useContext(ToursContext);

    const [form] = Form.useForm();
    const [targetType, setTargetType] = useState<ETourTargetType>(ETourTargetType.COUNTRY);
    const [workspacePage, setWorkspacePage] = useState(1);
    const [workspaceSearch, setWorkspaceSearch] = useState('');
    const [workspaceHasMore, setWorkspaceHasMore] = useState(true);
    const [workspaceLoadingMore, setWorkspaceLoadingMore] = useState(false);
    const [workspaceOptions, setWorkspaceOptions] = useState<any[]>([]);

    useEffect(() => {
        form.setFieldsValue({ targetIds: undefined });

        if (targetType === ETourTargetType.WORKSPACE) {
            if (!tourSelected || tourSelected.targetType !== ETourTargetType.WORKSPACE) {
                setWorkspaceOptions([]);
                setWorkspacePage(1);
                setWorkspaceHasMore(true);
            }
        }
    }, [targetType, form, tourSelected]);

    const handleWorkspaceDropdownVisibleChange = async (open: boolean) => {
        if (open && workspaceOptions.length === 0 && targetType === ETourTargetType.WORKSPACE && fetchWorkspacesAsync) {
            setWorkspaceLoadingMore(true);
            const data = await fetchWorkspacesAsync({ page: 1, pageSize: PAGE_SIZE, search: workspaceSearch });
            setWorkspaceOptions(data.items || []);
            setWorkspaceHasMore((data.items?.length || 0) === PAGE_SIZE);
            setWorkspaceLoadingMore(false);
        }
    };

    const handleWorkspaceScroll = async (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (
            scrollHeight - scrollTop - clientHeight < 32
            && !workspaceLoadingMore
            && workspaceHasMore
            && fetchWorkspacesAsync
        ) {
            setWorkspaceLoadingMore(true);
            const nextPage = workspacePage + 1;
            const data = await fetchWorkspacesAsync({ page: nextPage, pageSize: PAGE_SIZE, search: workspaceSearch });
            setWorkspaceOptions((prev) => {
                const ids = new Set(prev.map((ws: any) => ws.id));
                const newItems = (data.items || []).filter((ws: any) => !ids.has(ws.id));
                return [...prev, ...newItems];
            });
            setWorkspaceHasMore((data.items?.length || 0) === PAGE_SIZE);
            setWorkspacePage(nextPage);
            setWorkspaceLoadingMore(false);
        }
    };

    const debouncedWorkspaceSearch = React.useCallback(
        debounce(async (value: string) => {
            setWorkspaceSearch(value);
            setWorkspacePage(1);
            setWorkspaceLoadingMore(true);
            if (fetchWorkspacesAsync) {
                const data = await fetchWorkspacesAsync({ page: 1, pageSize: PAGE_SIZE, search: value });
                setWorkspaceOptions((data.items || []).filter((item: any, idx: number, arr: any[]) => arr.findIndex((i) => i._id === item._id) === idx));
                setWorkspaceHasMore((data.items?.length || 0) === PAGE_SIZE);
            }
            setWorkspaceLoadingMore(false);
        }, 400),
        [fetchWorkspacesAsync],
    );

    const handleWorkspaceSearch = (value: string) => {
        setWorkspaceOptions([]);
        debouncedWorkspaceSearch(value);
    };

    const handleSubmit = (values: any) => {
        if (!values.name || !values.targetType || !values.targetIds?.length) return;

        if (tourSelected) {
            createTour({
                ...tourSelected,
                name: values.name,
                description: values.description || '',
                path: currentPath,
                targetType: values.targetType,
                targetIds: values.targetIds,
            });
            completeTourConfiguration();
            return;
        }

        createTour({
            name: values.name,
            description: values.description || '',
            path: currentPath,
            targetType: values.targetType,
            targetIds: values.targetIds,
        });
        completeTourConfiguration();
    };

    // Función para cargar workspaces específicos por ID
    const loadSelectedWorkspaces = async (workspaceIds: string[]) => {
        if (!workspaceIds || workspaceIds.length === 0) return;

        try {
            setWorkspaceLoadingMore(true);
            const { data } = await getWorkspaces({ workspaceIDs: workspaceIds });
            const selectedWorkspaces = data.data || [];

            setWorkspaceOptions((prev) => {
                const existingIds = new Set(prev.map((ws: any) => ws._id));
                const newWorkspaces = selectedWorkspaces.filter((ws: any) => !existingIds.has(ws._id));
                return [...prev, ...newWorkspaces];
            });

            form.setFieldsValue({
                targetIds: data.data.map((ws: any) => ws._id),
            });
        } catch (error) {
            console.error('Error loading selected workspaces:', error);
        } finally {
            setWorkspaceLoadingMore(false);
        }
    };

    useEffect(() => {
        if (tourSelected) {
            form.setFieldsValue({
                name: tourSelected.name,
                description: tourSelected.description,
                targetType: tourSelected.targetType,
                targetIds: tourSelected.targetIds,
            });
            setTargetType(tourSelected.targetType);
        }
    }, [currentTour, form, tourSelected]);

    // useEffect separado para cargar workspaces seleccionados SOLO cuando cambia el tour seleccionado
    useEffect(() => {
        if (
            tourSelected
            && tourSelected.targetType === ETourTargetType.WORKSPACE
            && Array.isArray(tourSelected.targetIds)
            && tourSelected.targetIds.length > 0
        ) {
            // Verificar si los workspaces ya están cargados para evitar llamadas innecesarias
            const missingIds = tourSelected.targetIds.filter(
                (id: string) => !workspaceOptions.some((ws: any) => ws._id === id),
            );
            if (missingIds.length > 0) {
                loadSelectedWorkspaces(missingIds);
            }
        }
    }, [tourSelected]);

    useEffect(() => {
        form.setFieldsValue({
            path: currentPath,
        });
    }, [currentPath]);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            size="small"
            initialValues={{ targetType: 'country' }}
        >
            <Form.Item
                label="Nombre del Tour"
                name="name"
                rules={[{ required: true, message: 'El nombre es requerido' }]}
            >
                <Input placeholder="Ej: Tour de Creación de Pedidos" />
            </Form.Item>
            <Form.Item label="Descripción" name="description">
                <TextArea rows={2} placeholder="Describe brevemente el tour..." />
            </Form.Item>

            <Form.Item
                label="Ruta del Tour"
                name="path"
            >
                <Input disabled value={currentPath} />
            </Form.Item>

            <Form.Item label="Aplicado para" name="targetType">
                <Radio.Group
                    onChange={(e) => setTargetType(e.target.value)}
                >
                    <Radio value={ETourTargetType.COUNTRY}>País</Radio>
                    <Radio value={ETourTargetType.WORKSPACE}>Workspace</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item
                label={targetType === ETourTargetType.COUNTRY ? 'Seleccionar Países' : 'Seleccionar Workspaces'}
                name="targetIds"
                rules={[{
                    required: true,
                    message: targetType === ETourTargetType.COUNTRY
                        ? 'Selecciona al menos un país'
                        : 'Selecciona al menos un workspace',
                }]}
            >
                {targetType === ETourTargetType.COUNTRY ? (
                    <Select
                        mode="multiple"
                        placeholder="Selecciona países..."
                        loading={loadingCountries}
                        allowClear
                    >
                        {countries.map((country: any) => (
                            <Option key={country.id} value={country.id}>
                                {country.name}
                                {' '}
                                (
                                {country.code}
                                )
                            </Option>
                        ))}
                    </Select>
                ) : (
                    <Select
                        mode="multiple"
                        placeholder="Selecciona workspaces..."
                        loading={workspaceLoadingMore}
                        allowClear
                        showSearch
                        filterOption={false}
                        onSearch={handleWorkspaceSearch}
                        onPopupScroll={handleWorkspaceScroll}
                        onDropdownVisibleChange={handleWorkspaceDropdownVisibleChange}
                        notFoundContent={workspaceLoadingMore ? <Spin size="small" /> : null}
                        value={workspaceOptions.map((ws: any) => ws._id)}
                    >
                        {workspaceOptions.map((ws: any) => (
                            <Option key={ws._id} value={ws._id}>
                                {ws.name}
                            </Option>
                        ))}
                    </Select>
                )}
            </Form.Item>
            <div style={{ marginTop: 16 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button htmlType="submit" type="primary" size="small">
                        Continuar
                    </Button>
                    <Button
                        type="default"
                        size="small"
                        onClick={cancelAddingTour}
                    >
                        Cancelar
                    </Button>
                </Space>
            </div>
        </Form>
    );
};

export default TourInitialForm;
